import { AgoraEventHandler } from "@/agora";
import { ErrorLocale, TeacherActionLocale, TeacherClassError } from "@/locales/localeid";
import { AnnotationMode, RoomModel, TeachingMode } from "@/models";
import { GLErrorCode } from "@/models/error.model";
import { UpdateLessonAndUnitModel } from "@/models/update-lesson-and-unit.model";
import router from "@/router";
import {
  FetchSessionDtoResModel,
  HelperService,
  InfoService,
  OneToOneDto,
  RemoteTeachingService,
  TeacherService,
  TeacherUpdateAlternateState,
  TeacherUpdateMediaStateRequestModel,
  ToggleStudentMediaDevicesAsync,
  ToggleStudentPaletteAsync,
  mapSessionOneToOneForHelper,
  mapSessionOneToOneForTeacher,
} from "@/services";
import { GetIndependentStudentsResponse, IndependentService } from "@/services/independent";
import { store } from "@/store";
import { UserRole } from "@/store/app/state";
import { callingUtils } from "@/store/room/teacher/utils";
import { StudyMode } from "@/store/teaching/interfaces";
import { applySettings } from "@/store/realtime-settings/utils";
import { AGORA_MINIMUM_USER_VOLUME, MIN_ZOOM_RATIO } from "@/utils/constant";
import { Logger } from "@/utils/logger";
import { Paths } from "@/utils/paths";
import { getCurrentUTCUnixTimestamp, getDeviceId, MediaDeviceTypes, showUnableJoinClassErrorNotification } from "@/utils/utils";
import { EndExposureModel, HelperToggleTeacherAudioModel, REALTIME_EVENT, TeacherAction, UpdateExposureModel } from "@/ws";
import { ConnectionDisconnectedReason, ConnectionState, IAgoraRTCRemoteUser, UID } from "agora-rtc-sdk-ng";
import { notification } from "ant-design-vue";
import { ErrorCode, fmtMsg } from "vue-glcommonui";
import { ActionTree } from "vuex";
import { DefaultPayload, DeviceMediaPayload, InitClassRoomPayload, NetworkQualityPayload } from "../interface";
import { ParamsToJoinCurSessionInterface } from "./../interface";
import { useTeacherRealtimeHandler } from "./handler";
import { TeacherRoomState } from "./state";
import { CanvasObjectModel, ZoomSlidePositionModel, ZoomSlideModel } from "@/hooks/use-remote-requests.";
import { ExposureDetailType } from "@/views/teacher-class/components/lesson-plan/lesson-plan";
import { defineVuexName, vuexName, MODULES } from "@/store/utils";
import { AntdNotificationType } from "@/utils/type";
import { showAPIErrorNotification, showNotification } from "@/utils/antd-utils";
import { MediaUserPublishStatus } from "@/store/calling/utils";
import { CallingUserStatus } from "@/store/calling/mutations";
import { mapObjectsFromBE } from "@/store/annotation-v2/actions";
import { ClassActionFromValue } from "../student/state";
import { REALTIME_CHANNEL, SessionRealtimeChannelKey, RealtimeClient } from "@/ws";

function canSendRealtimeAction(rootGetters: any, _context: string): boolean {
  const canSendRealtime = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_CAN_SEND_REALTIME_ACTION)];

  if (canSendRealtime) {
    return true;
  }

  showNotification({
    type: AntdNotificationType.ERROR,
    message: fmtMsg(TeacherActionLocale.WaringTitle),
    description: "Realtime connection is still initializing. Please wait a few seconds and try again.",
  });
  return false;
}

/**
 * Validate and get the appropriate channel for sending realtime messages
 * @returns The validated channel key or null if validation fails
 */
function validateAndGetChannel(
  realtimeTransport: RealtimeClient,
  oneOneId: string | null | undefined,
  isHelper: boolean,
): { channelKey: SessionRealtimeChannelKey; sendMethod: (config: any) => Promise<boolean> } | null {
  // Determine expected channel based on teaching mode and role
  let expectedChannelKey: SessionRealtimeChannelKey;
  let sendMethod: (config: any) => Promise<boolean>;

  if (oneOneId) {
    // If in one-to-one mode, use appropriate channel based on role
    if (isHelper) {
      expectedChannelKey = REALTIME_CHANNEL.HELPER_ONE_ONE;
      sendMethod = realtimeTransport.sendToHelperOneOne.bind(realtimeTransport);
    } else {
      expectedChannelKey = REALTIME_CHANNEL.TEACHER_ONE_ONE;
      sendMethod = realtimeTransport.sendToTeacherOneOne.bind(realtimeTransport);
    }
  } else {
    // If not in one-to-one mode, use main channel
    expectedChannelKey = REALTIME_CHANNEL.MAIN;
    sendMethod = realtimeTransport.sendToMain.bind(realtimeTransport);
  }

  // Validate user is actually joined to the expected channel
  const joinedChannels = realtimeTransport.getJoinedChannels?.();
  if (joinedChannels) {
    // Get the actual channel name from the channel key
    const expectedChannelName = realtimeTransport.getChannelNameByKey?.(expectedChannelKey);

    if (!expectedChannelName) {
      if (joinedChannels.length === 0) {
        return null;
      }

      showNotification({
        type: AntdNotificationType.ERROR,
        message: fmtMsg(TeacherActionLocale.WaringTitle),
        description: "Channel configuration error. Please reload the page to continue.",
      });
      return null;
    }

    const isJoinedToExpectedChannel = joinedChannels.includes(expectedChannelName);

    if (!isJoinedToExpectedChannel) {
      if (joinedChannels.length === 0) {
        return null;
      }
      showNotification({
        type: AntdNotificationType.ERROR,
        message: fmtMsg(TeacherActionLocale.WaringTitle),
        description: "Connection state mismatch. Please reload the page to continue.",
      });
      return null;
    }
  }

  return {
    channelKey: expectedChannelKey,
    sendMethod,
  };
}

function splitStudentsBySpacePresence(
  rootGetters: any,
  studentIds: string[],
): {
  online: string[];
  offline: string[];
} {
  const isPresenceReady = rootGetters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_IS_INITIALIZED)];
  if (!isPresenceReady) {
    return { online: [...studentIds], offline: [] };
  }

  const onlineStudentIds: Set<string> = rootGetters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_STUDENT_USER_IDS)] ?? new Set<string>();

  const online: string[] = [];
  const offline: string[] = [];

  studentIds.forEach((id) => {
    if (onlineStudentIds.has(id)) {
      online.push(id);
    } else {
      offline.push(id);
    }
  });

  return { online, offline };
}

function formatMissingStudentNames(studentIds: string[], studentMap: Map<string, { name: string; englishName?: string }>): string {
  const names = studentIds
    .map((id) => {
      const student = studentMap.get(id);
      return student?.englishName || student?.name || id;
    })
    .slice(0, 5);

  if (studentIds.length > 5) {
    return `${names.join(", ")} and ${studentIds.length - 5} other students`;
  }
  return names.join(", ");
}

const actions: ActionTree<TeacherRoomState, any> = {
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.END_CLASS)]({ dispatch, state, commit }, payload: DefaultPayload) {
    if (state.info) {
      const { markAsComplete } = payload;
      await RemoteTeachingService.teacherEndClassRoom(state.info?.id, markAsComplete);
      if (markAsComplete) {
        TeacherService.updateUnitLessonNumber(
          state.info.classInfo.classId,
          state.info.classInfo.unit,
          state.info.classInfo.lesson,
          state.user?.id ?? "",
        ).catch((err) => Logger.log(err));
      }
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_SESSION_ENDED, false), true);
    }
    await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.RESET_CLASS, false));
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.RESET_CLASS)]({ commit }, endClass = true) {
    if (endClass) {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.END_CLASS, false));
    } else {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.LEAVE_CLASS, false));
    }
    commit(vuexName(MODULES.LESSON.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.CALLING.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.REALTIME_SETTINGS.COMMITS.RESET_STATE), null, { root: true });
    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.END_CLASS), null, { root: true });
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.HELPER_EXIT_CLASS)]({ commit, state, dispatch }, byTeacherRemove: boolean) {
    if (state.info && !byTeacherRemove) {
      try {
        await HelperService.exitSession();
      } catch (error) {
        // Logger.error(error);
      }
    }
    await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.RESET_CLASS, false));
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_IS_HELPER), false, { root: true });
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHING_MODE_ASYNC)]({ rootState, commit }, mode: TeachingMode) {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHING_MODE, false), mode);
    const sessionId = rootState.classTeaching.sessionId;

    try {
      await RemoteTeachingService.setLessonPlanCollapse(sessionId, mode);
    } catch (error) {
      // Logger.error(error);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.MANAGE_REMOTE_USER_VIDEO_AND_AUDIO_SUBSCRIPTIONS)]({ state, rootGetters }) {
    const { manager, idOne: oneToOneStudentId, idOneWithAnotherTeacher: oneToOneWithAnotherTeacherStudentId, teacher, helper } = state;
    const meId: string | undefined = rootGetters[vuexName(MODULES.AUTH.GETTERS.GET_USER_ID)];

    if (!meId || !teacher || !manager?.agoraClient?.joined) return;

    const isHelperJoinClass = !!helper;
    const isMeHelper = isHelperJoinClass && meId === helper.id;

    let remoteVideoUserIds: string[] = rootGetters[vuexName(MODULES.CALLING.GETTERS.GET_VIDEO_USER_IDS)].filter((id: string) => id !== meId);
    let remoteAudioUserIds: string[] = rootGetters[vuexName(MODULES.CALLING.GETTERS.GET_AUDIO_USER_IDS)].filter((id: string) => id !== meId);

    // If the current user is a teacher, only subscribe to the helper's video if the helper is allowed to share video
    if (!isMeHelper && isHelperJoinClass && !helper.isVideoShownByTeacher) {
      remoteVideoUserIds = remoteVideoUserIds.filter((id: string) => id !== helper.id);
    }

    // If I'm in one-to-one mode with a student, only subscribe to the student's video and audio
    if (oneToOneStudentId) {
      if (isMeHelper) {
        remoteVideoUserIds = remoteVideoUserIds.filter((id: string) => id === oneToOneStudentId || id === teacher.id);
        const isMuteTeacher = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_IS_MUTE_TEACHER_AUDIO_WHEN_ONE_TO_ONE_WITH_HELPER)];
        remoteAudioUserIds = remoteAudioUserIds.filter((id: string) => id === oneToOneStudentId || (id === teacher.id && !isMuteTeacher));
      } else {
        remoteVideoUserIds = remoteVideoUserIds.filter((id: string) => id === oneToOneStudentId);
        remoteAudioUserIds = remoteAudioUserIds.filter((id: string) => id === oneToOneStudentId);
      }
    }

    // If another teacher is in one-to-one mode with a student, remove both of them from the video and audio subscriptionss
    if (oneToOneWithAnotherTeacherStudentId) {
      if (isMeHelper) {
        remoteVideoUserIds = remoteVideoUserIds.filter((id: string) => id !== oneToOneWithAnotherTeacherStudentId && id !== teacher.id);
        remoteAudioUserIds = remoteAudioUserIds.filter((id: string) => id !== oneToOneWithAnotherTeacherStudentId && id !== teacher.id);
      } else if (isHelperJoinClass) {
        remoteVideoUserIds = remoteVideoUserIds.filter((id: string) => id !== oneToOneWithAnotherTeacherStudentId && id !== helper.id);
        remoteAudioUserIds = remoteAudioUserIds.filter((id: string) => id !== oneToOneWithAnotherTeacherStudentId && id !== helper.id);
      }
    }
    return manager.updateAudioAndVideoFeed([...new Set(remoteVideoUserIds)], [...new Set(remoteAudioUserIds)]);
  },
  //leaveRoom
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.LEAVE_ROOM)]({ state, commit }, _payload: { leave: boolean }) {
    await state.manager?.close(_payload.leave);
    // reset module's state
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.LEAVE_CLASS, false));
    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.END_CLASS), null, { root: true });
  },
  //Send request notify to join room
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.ENTER_CLASS)](store, _rejoin = false) {
    try {
      const { info, manager, teacher } = store.state;
      if (!info || !manager) return;

      const userId = store.rootGetters[vuexName(MODULES.AUTH.GETTERS.GET_USER_ID)] as string | undefined;
      const isHelper = !!teacher && userId !== teacher.id;

      if (isHelper) {
        await RemoteTeachingService.helperEnterClass();
      } else {
        await RemoteTeachingService.teacherEnterClass();
      }

      // Register handlers once per manager instance
      // (avoid duplicated handlers when rejoin triggers this action multiple times)
      if (!(manager as any).__teacherRoomHandlersRegistered) {
        // IMPORTANT: Register realtime handlers and ensure they're ready before any switchChannels() calls
        // This prevents the race condition where channels are created before handlers are subscribed
        await manager.registerRealtimeEventHandler(useTeacherRealtimeHandler(store));
        (manager as any).__teacherRoomHandlersRegistered = true;
      }
    } catch (error) {
      Logger.error("ENTER_CLASS", error);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.JOIN_ROOM)](store) {
    const { state, rootGetters, commit } = store;
    const needConfirmToHandOver = rootGetters[vuexName(MODULES.APP.GETTERS.GET_REPLACED_CLASS_ID, false)];
    if (needConfirmToHandOver) return;
    if (!state.info || !state.teacher || !state.manager) return;
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_REALTIME_READY, false), false);
    const {
      bandwidthUpdateBasedOnAgora,
      remoteUserJoined,
      remoteUserLeft,
      localUserConnectionChanged,
      handleUserPublishedAudio,
      handleUserPublishedVideo,
    } = callingUtils(store);
    const callingEventHandlers: AgoraEventHandler = {
      onLocalNetworkUpdate: (payload: NetworkQualityPayload) => {
        bandwidthUpdateBasedOnAgora(payload);
      },
      onUserJoined: ({ uid }: IAgoraRTCRemoteUser) => {
        const id = typeof uid === "string" ? uid : uid.toString();
        remoteUserJoined(id);
      },
      onUserLeft: async ({ uid }: IAgoraRTCRemoteUser, _: string) => {
        const id = typeof uid === "string" ? uid : uid.toString();
        await remoteUserLeft(id);
      },
      onConnectionStateChange: (curState: ConnectionState, prevState: ConnectionState, reason: ConnectionDisconnectedReason | undefined) => {
        localUserConnectionChanged(curState, prevState, reason);
      },
      onException: () => {
        //
      },
      onUserPublished: (userId: string, mediaType: "audio" | "video") => {
        if (mediaType === "audio") {
          handleUserPublishedAudio({ id: userId, status: MediaUserPublishStatus.PUBLISHED });
        } else if (mediaType === "video") {
          handleUserPublishedVideo({ id: userId, status: MediaUserPublishStatus.PUBLISHED });
        }
      },
      onUserUnPublished: (userId: string, mediaType: "audio" | "video") => {
        if (mediaType === "audio") {
          handleUserPublishedAudio({ id: userId, status: MediaUserPublishStatus.UNPUBLISHED });
        } else if (mediaType === "video") {
          handleUserPublishedVideo({ id: userId, status: MediaUserPublishStatus.UNPUBLISHED });
        }
      },
    };
    await state.manager?.join({
      classId: state.info.id,
      teacherId: state.user?.id,
      idOne: state.idOne,
      callingEventHandlers,
    });

    await store.dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.UP_TO_DATE_SESSION_AFTER_RECONNECTED, false));
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_IS_REALTIME_READY, false), true);
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.UP_TO_DATE_SESSION_AFTER_RECONNECTED)]({ rootState, commit, dispatch }) {
    let isUserOneOne = false;
    const isHelper = rootState.teacher.isHelper;
    const sessionId = rootState.classTeaching.sessionId;
    let resp: FetchSessionDtoResModel | null = null;
    try {
      resp = await RemoteTeachingService.fetchSessionDtoById(sessionId);
    } catch (e) {
      Logger.error(e);
      return;
    }
    if (isHelper) {
      resp = mapSessionOneToOneForHelper(resp);
    } else {
      resp = mapSessionOneToOneForTeacher(resp);
    }
    const roomInfo = resp.data;
    applySettings(commit, roomInfo.settings);
    /** If current lesson is "alternative" type, fetch media state to update the media's progress */
    commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
    commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), roomInfo.state.independentMode ? StudyMode.Independent : StudyMode.Normal, {
      root: true,
    });
    commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), roomInfo.state.isTeamMode, { root: true });
    if (roomInfo.helper) {
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), true, { root: true });
    }
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ROOM_INFO, false), { ...roomInfo, options: { isActionUpToDate: true, isHelper } });

    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID, false), {
      id: roomInfo.oneToOneWithAnotherTeacherStudentId,
    });

    const { oneAndOneDto } = roomInfo;
    if (oneAndOneDto) {
      isUserOneOne = true;
      const {
        isShowWhiteBoard,
        isIgnoreTeacherAudio,
        studentId,
        isEnablePalette,
        ratio,
        ratioScrollPdf,
        position,
        selectedExposureId,
        selectedExposureItemId,
      } = oneAndOneDto;
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), oneAndOneDto, { root: true });
      commit(
        vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE),
        {
          id: selectedExposureId,
          preventSelectFirstSlideAutomatically: !!selectedExposureItemId,
        },
        { root: true },
      );
      commit(
        vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA),
        {
          id: selectedExposureItemId,
        },
        { root: true },
      );

      if (isHelper) {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), isIgnoreTeacherAudio, {
          root: true,
        });
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD, false), isShowWhiteBoard);
      }

      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
        studentId,
        isPaletteEnabled: isEnablePalette,
      });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_CLASS_ACTION, false), {
        action: ClassActionFromValue(oneAndOneDto.lessonAction),
      });

      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: studentId }, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), ratio ? ratio : MIN_ZOOM_RATIO, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), ratioScrollPdf ?? 0, { root: true });
      if (position) {
        commit(
          vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS),
          {
            x: position.x,
            y: position.y,
          },
          { root: true },
        );
      }
    } else {
      isHelper && commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD, false), roomInfo.state.isShowWhiteBoard);
      await dispatch(vuexName(MODULES.LESSON.DISPATCHES.SET_BOTH_EXPOSURE_AND_SLIDE), roomInfo.lessonPlan, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), roomInfo.lessonPlan.ratio ? roomInfo.lessonPlan.ratio : MIN_ZOOM_RATIO, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), roomInfo.state.ratioScrollPdf ?? 0, { root: true });
      if (roomInfo.lessonPlan.position) {
        commit(
          vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS),
          {
            x: roomInfo.lessonPlan.position.x,
            y: roomInfo.lessonPlan.position.y,
          },
          { root: true },
        );
      }
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "" }, { root: true });
    }

    // full sync targets
    await dispatch(
      vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API),
      isUserOneOne ? roomInfo.annotation?.oneOneDrawing?.targets : roomInfo.annotation?.drawing?.targets,
      {
        root: true,
      },
    );

    // full sync objects
    commit(
      vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
      {
        objects: mapObjectsFromBE(isUserOneOne ? roomInfo.annotation?.oneOneDrawing?.objects : roomInfo.annotation?.drawing?.objects),
      },
      { root: true },
    );
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.INIT_CLASS_ROOM)]({ commit, dispatch, rootState }, payload: InitClassRoomPayload) {
    try {
      let isUserOneOne = false;
      /** Archive independent mode data */
      const setIndependentModeData = async (currentUnit: number, currentLesson: number) => {
        let response: null | GetIndependentStudentsResponse;
        if (payload.isHelper) {
          response = await IndependentService.helperGetIndependentStudentsItem();
        } else {
          response = await IndependentService.getIndependentStudentsItem();
        }
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_MODE), StudyMode.Independent, { root: true });
        commit(vuexName(MODULES.TEACHER_TEACHING.COMMITS.SET_INDEPENDENT_LIST_STUDENT_ITEMS), response.data, { root: true });
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INDEPENDENT_MODE_CURRENT_UNIT), currentUnit, { root: true });
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INDEPENDENT_MODE_CURRENT_LESSON), currentLesson, { root: true });
      };
      const setClassTeachingData = (sessionInfo: { sessionId: string; groupId: string; classId: string }) => {
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_SESSION_INFO), sessionInfo, { root: true });
      };
      /** Archive helper data */
      const setOneToOneWithHelperData = (oneToOneWithHelperDto: OneToOneDto) => {
        const { isIgnoreTeacherAudio } = oneToOneWithHelperDto;
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), isIgnoreTeacherAudio, { root: true });
      };
      /** Archive team mode data */
      const setTeamModeData = async (isTeamMode: boolean) => {
        commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE), isTeamMode, { root: true });
        await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS), null, { root: true });
      };
      /** Init the class */
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_USER, false), { id: payload.userId, name: payload.userName });
      let roomResponse: FetchSessionDtoResModel;
      if (payload.isHelper && payload.groupId) {
        roomResponse = await HelperService.fetchSessionDataAsHelper(payload.groupId, payload.deviceId);
      } else {
        roomResponse = await RemoteTeachingService.fetchSessionDataAsTeacher(payload.deviceId);
      }
      const roomInfo: RoomModel = roomResponse.data;
      if (roomInfo?.classInfo?.classId !== payload.classId) {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ERROR, false), {
          errorCode: GLErrorCode.CLASS_IS_NOT_ACTIVE,
          message: fmtMsg(ErrorLocale.ClassNotStarted),
        });
        return Promise.reject(GLErrorCode.CLASS_IS_NOT_ACTIVE);
      }
      setClassTeachingData({ sessionId: roomInfo.id, classId: roomInfo.classInfo.classId, groupId: roomInfo.classInfo.groupId });
      /** If current lesson is "alternative" type, fetch media state to update the media's progress */
      store.commit(vuexName(MODULES.WHITE_BOARD.COMMITS.SET_SHOULD_FETCH_MEDIA_STATE), true, { root: true });
      await setTeamModeData(roomInfo.state.isTeamMode);
      if (payload.isHelper && roomInfo.helperOneAndOneDto?.studentId) {
        setOneToOneWithHelperData(roomInfo.helperOneAndOneDto);
      }
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_PARAMS_FOR_HELPER_JOIN_SESSION, false), {
        deviceId: payload.deviceId,
        groupId: payload.groupId || "",
      });
      if (roomInfo.helper) {
        commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), true, { root: true });
      }
      applySettings(commit, roomInfo.settings);
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ROOM_INFO, false), roomInfo);
      if (payload.isHelper && payload.callFirstTime) {
        // Since we just joined and haven't received the teacher join room message from Agora yet,
        // when logging in as a helper, we consider the teacher as disconnected
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_INITIAL_TEACHER_DISCONNECT_STATUS, false));
      }

      commit(
        vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
        {
          objects: mapObjectsFromBE(roomInfo?.annotation?.drawing?.objects),
        },
        { root: true },
      );

      if (!payload.callFirstTime && !roomInfo.lessonPlan.selectedExposureId) {
        // Clear exposure and exposure item if not set on Redis after successful reconnection
        commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_EXPOSURE), null, { root: true });
      }
      await dispatch(
        vuexName(MODULES.LESSON.DISPATCHES.SET_INFO),
        {
          lessonPlan: roomInfo.lessonPlan,
          isSetCurrentExposure: roomInfo.lessonPlan.selectedExposureId && !roomInfo.oneAndOneDto?.studentId,
        },
        { root: true },
      );
      if (roomInfo.state.independentMode) {
        await setIndependentModeData(roomInfo.classInfo.unit, roomInfo.classInfo.lesson);
      }
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_WITH_ANOTHER_TEACHER_ID, false), {
        id: roomInfo.oneToOneWithAnotherTeacherStudentId,
      });
      const { oneAndOneDto } = roomInfo;
      if (oneAndOneDto) {
        isUserOneOne = true;
        const { selectedExposureId, selectedExposureItemId, isShowWhiteBoard } = oneAndOneDto;
        commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_ONE_TO_ONE_DATA), oneAndOneDto, { root: true });
        store.commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE), {
          id: selectedExposureId,
          preventSelectFirstSlideAutomatically: !!selectedExposureItemId,
        });
        store.commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA), { id: selectedExposureItemId });
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD, false), isShowWhiteBoard);
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_CLASS_ACTION, false), {
          action: ClassActionFromValue(oneAndOneDto.lessonAction),
        });
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: oneAndOneDto.studentId }, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), oneAndOneDto.ratio ? oneAndOneDto.ratio : MIN_ZOOM_RATIO, { root: true });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), oneAndOneDto.ratioScrollPdf ?? 0, { root: true });
        if (oneAndOneDto.position) {
          commit(
            vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS),
            {
              x: oneAndOneDto.position.x,
              y: oneAndOneDto.position.y,
            },
            { root: true },
          );
        }
      } else {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD, false), roomInfo.state.isShowWhiteBoard);
        commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), roomInfo.lessonPlan.ratio ? roomInfo.lessonPlan.ratio : MIN_ZOOM_RATIO, {
          root: true,
        });
        commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), roomInfo.state.ratioScrollPdf ?? 0, { root: true });
        if (roomInfo.lessonPlan.position) {
          commit(
            vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS),
            {
              x: roomInfo.lessonPlan.position.x,
              y: roomInfo.lessonPlan.position.y,
            },
            { root: true },
          );
        }
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_ONE_ID), { id: "" }, { root: true });
      }

      // full sync targets
      await dispatch(
        vuexName(MODULES.LESSON.DISPATCHES.PROCESS_VISIBLE_TARGETS_DATA_FROM_API),
        isUserOneOne ? roomInfo.annotation?.oneOneDrawing?.targets : roomInfo.annotation?.drawing?.targets,
        {
          root: true,
        },
      );
      // full sync objects
      commit(
        vuexName(MODULES.ANNOTATION_V2.COMMITS.SET_OBJECTS),
        {
          objects: mapObjectsFromBE(isUserOneOne ? roomInfo.annotation?.oneOneDrawing?.objects : roomInfo.annotation?.drawing?.objects),
        },
        { root: true },
      );

      commit(
        vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_INITIAL_CLASS_DATA_SNAPSHOT),
        {
          exposureId: roomInfo.lessonPlan.selectedExposureId,
        },
        {
          root: true,
        },
      );
    } catch (err) {
      Logger.error(err);
      if (rootState.teacherRoom.isDisconnected) return;

      let errorCode = 12; // default error code
      let errorMessage = "";

      if (err.code === ErrorCode.ConcurrentUserException) {
        await dispatch(
          vuexName(MODULES.MODAL.DISPATCHES.CONFIRM_REPLACEMENT_SESSION),
          async () => {
            const resp = payload.isHelper
              ? await RemoteTeachingService.sessionReplacementAccept4Helper(getDeviceId())
              : await RemoteTeachingService.sessionReplacementAccept4Teacher(getDeviceId());
            if (resp?.success) location.reload();
          },
          { root: true },
        );
        return;
      }

      if (err.code && typeof err.code === "number") {
        errorCode = err.code;
      }

      if (err.message) {
        errorMessage = err.message;
      }
      showUnableJoinClassErrorNotification(errorCode, errorMessage);
      await router.push(Paths.Teacher);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.FORCE_OUT_HELPER_ONE_TO_ONE)]({ rootGetters, dispatch }) {
    const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    const idOneWithAnotherTeacher = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID)];
    const idOne = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];

    if ((idOneWithAnotherTeacher && !isHelper) || (isHelper && idOne)) {
      await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SEND_ONE_AND_ONE, false), {
        enabled: false,
        id: null,
        isHelper: true,
      });
    }
  },
  [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_SPEAKING_USERS)]({ commit }, payload: { level: number; uid: UID }[]) {
    const validSpeakings: Array<string> = [];
    if (payload) {
      payload.map((item) => {
        if (item.level >= AGORA_MINIMUM_USER_VOLUME) {
          // should check by a level
          validSpeakings.push(item.uid?.toString());
        }
      });
    }
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_SPEAKING_USERS, false), { userIds: validSpeakings });
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHER_AUDIO)]({ state }, payload: DeviceMediaPayload) {
    try {
      await state.manager?.setMicrophone({ enable: payload.enable });
    } catch (err) {
      Logger.error(err);
      notification.error({
        message: fmtMsg(ErrorLocale.ToggleMicroError),
      });
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHER_VIDEO)]({ state }, payload: DeviceMediaPayload) {
    try {
      await state.manager?.setCamera({
        enable: payload.enable,
        videoEncoderConfigurationPreset: "480p",
      });
    } catch (err) {
      Logger.error(err);
      notification.error({
        message: fmtMsg(ErrorLocale.ToggleCameraError),
      });
    }
  },
  // Intentionally fire-and-forget: no need to wait for the response
  // This action sends the request without blocking the UI
  [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.END_EXPOSURE)]({ state, rootState, rootGetters }, payload: { id: string }) {
    const sessionId = rootState.classTeaching.sessionId;
    if (!sessionId) return;

    const request: EndExposureModel = {
      sessionId: sessionId,
      exposureId: payload.id,
    };

    const realtimeTransport = state.manager?.WSClient.getRealtimeTransport();
    const oneOneId = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];

    if (realtimeTransport && canSendRealtimeAction(rootGetters, "END_EXPOSURE")) {
      const channelInfo = validateAndGetChannel(realtimeTransport, oneOneId, false);

      if (channelInfo) {
        channelInfo.sendMethod({
          event: REALTIME_EVENT.TEACHER,
          action: TeacherAction.END_EXPOSURE,
          data: {
            exposureId: request.exposureId,
            needUpdateRemainingTime: !oneOneId,
          },
        });
      }
    }

    try {
      // Call API to update cache data
      RemoteTeachingService.endExposure(request);
    } catch (error) {
      Logger.error(error);
      RemoteTeachingService.endExposure(request);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.REQUEST_CHANGE_EXPOSURE_ITEM)](
    { state, rootState, rootGetters },
    payload: { exposureId: string; exposureItemId: string },
  ) {
    const sessionId = rootState.classTeaching.sessionId;
    if (!sessionId) return;

    const request: UpdateExposureModel = {
      sessionId: sessionId,
      exposureId: payload.exposureId,
      exposureItemId: payload.exposureItemId,
      timestamp: getCurrentUTCUnixTimestamp(),
    };

    const realtimeTransport = state.manager?.WSClient.getRealtimeTransport();
    const oneOneId = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];

    if (realtimeTransport && canSendRealtimeAction(rootGetters, "REQUEST_CHANGE_EXPOSURE_ITEM")) {
      const channelInfo = validateAndGetChannel(realtimeTransport, oneOneId, false);

      if (channelInfo) {
        await channelInfo.sendMethod({
          event: REALTIME_EVENT.TEACHER,
          action: TeacherAction.UPDATE_EXPOSURE,
          data: {
            exposureId: request.exposureId,
            exposureItemId: request.exposureItemId,
            timestamp: request.timestamp,
          },
          senderId: rootState.auth.userInfo?.id,
        });
      }
    }

    try {
      // Call API to update cache data
      await RemoteTeachingService.updateExposure(request);
    } catch (error) {
      Logger.error(error);
      await RemoteTeachingService.updateExposure(request);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_CLASS_ACTION)]({ rootState }, payload: { action: number }) {
    const sessionId = rootState.classTeaching.sessionId;
    if (!sessionId) return;

    try {
      const isHelper = rootState.teacher.isHelper;
      await RemoteTeachingService.setLessonAction(sessionId, payload.action, isHelper);
    } catch (error) {
      Logger.error(error);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_ANNOTATION_MODE)](_, payload: { mode: AnnotationMode }) {
    await RemoteTeachingService.teacherUpdateAnnotationMode(payload.mode);
    // do nothing
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.UPDATE_ZOOM_SLIDE)](_, payload: ZoomSlideModel) {
    await RemoteTeachingService.teacherZoomSlide(payload);
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_MOVE_ZOOMED_SLIDE)](_, payload: ZoomSlidePositionModel) {
    await RemoteTeachingService.teacherMoveZoomedSlide(payload);
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SEND_ONE_AND_ONE)](
    { rootGetters },
    payload: { enabled: boolean; id: string | null; isHelper: boolean },
  ) {
    const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
    if (!sessionId) {
      return;
    }
    try {
      const studentId = payload.enabled ? payload.id : null;
      if (payload.isHelper) {
        await HelperService.helperSetOneOne(sessionId, studentId);
      } else {
        await RemoteTeachingService.teacherSetOneOne(sessionId, studentId);
      }
    } catch (error) {
      Logger.error(error);
      notification.error({
        message: fmtMsg(TeacherClassError.FailSetOneOneMode),
      });
    }
  },
  //setWhiteboard
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_WHITEBOARD)]({ rootGetters }, { isShowWhiteBoard }: { isShowWhiteBoard: boolean }) {
    const sessionId = rootGetters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_SESSION_ID)];
    if (!sessionId) return;
    try {
      await RemoteTeachingService.teacherSetWhiteboard(isShowWhiteBoard);
    } catch (error) {
      Logger.error(error);
    }
  },
  //setMediaState
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_MEDIA_STATE)]({ state, rootState }, payload: TeacherUpdateAlternateState) {
    const sessionId = rootState.classTeaching.sessionId;
    const model: TeacherUpdateMediaStateRequestModel = {
      ...payload,
      sessionId,
      isOneToOne: !!state.idOne,
    };
    await RemoteTeachingService.teacherUpdateMediaState(model);
  },
  //setOnline
  [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_ONLINE)]({ commit }) {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ONLINE, false));
  },
  //setOffline
  [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_OFFLINE)]({ commit, rootState }) {
    if (rootState.app.userRole === UserRole.Teacher) {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_OFFLINE, false));
    }
  },
  //getAvatarTeacher
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.GET_AVATAR_TEACHER)]({ commit }, payload: { teacherId: string }) {
    const response = await InfoService.fetchTeacherAvatarUrl(payload.teacherId);
    if (response) commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_AVATAR_TEACHER, false), response);
  },
  //getAvatarHelper
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.GET_AVATAR_HELPER)]({ commit }, payload: { helperId: string }) {
    const response = await InfoService.fetchTeacherAvatarUrl(payload.helperId);
    if (response) commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_AVATAR_HELPER, false), response);
  },
  //setLessonAndUnit
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_LESSON_AND_UNIT)](
    { commit, state, dispatch, rootGetters, getters },
    p: {
      unit: number;
      lesson: number;
      unitId: number;
      isCompleted: boolean;
    } | null,
  ) {
    if (!state.info?.id) {
      return;
    }

    const updateState = async (roomInfo: RoomModel) => {
      commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_LESSON_DATA), null, { root: true });
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_ROOM_INFO, false), roomInfo);
      await dispatch(
        vuexName(MODULES.LESSON.DISPATCHES.SET_INFO),
        {
          lessonPlan: roomInfo.lessonPlan,
          isSetCurrentExposure: roomInfo.lessonPlan.selectedExposureId && !roomInfo.oneAndOneDto?.studentId,
        },
        { root: true },
      );
      commit(vuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO), MIN_ZOOM_RATIO, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), 0, { root: true });
      commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), undefined, { root: true });
    };

    if (p) {
      const data: UpdateLessonAndUnitModel = {
        unit: p.unit,
        lesson: p.lesson,
        unitId: p.unitId,
        sessionId: state.info?.id as string,
        isCompleted: p.isCompleted,
      };
      const roomInfo = await RemoteTeachingService.teacherUpdateLessonAndUnit(data);
      if (p.isCompleted) {
        TeacherService.updateUnitLessonNumber(
          state.info?.classInfo.classId ?? "",
          state.info?.classInfo.unit ?? 0,
          state.info?.classInfo.lesson ?? 0,
          state.user?.id ?? "",
        ).catch((err) => {
          Logger.log(err);
        });
      }
      await updateState(roomInfo);
    } else {
      let roomResponse = null;
      const params: ParamsToJoinCurSessionInterface = getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_PARAMS_TO_JOIN_CUR_SESSION, false)];
      const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
      if (isHelper) {
        roomResponse = await HelperService.fetchSessionDataAsHelper(params.groupId, params.deviceId);
      } else {
        roomResponse = await RemoteTeachingService.fetchSessionDataAsTeacher(params.deviceId);
      }
      const roomInfo: RoomModel = roomResponse.data;
      await updateState(roomInfo);
    }
  },
  //teacherRemoveHelper
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.TEACHER_REMOVE_HELPER)]({ state, commit }) {
    try {
      const helperId = state.helper?.id;
      if (!helperId) return;
      const { success, code } = await HelperService.teacherRemoveHelper(helperId);
      // reset the helper state
      if (success) {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_HELPER_INFO, false), undefined);
      } else {
        if (code == GLErrorCode.HELPER_IS_IN_1_1) {
          notification.warning({
            key: state.teacher?.id,
            message: fmtMsg(TeacherClassError.FailRemoveHelper),
          });
        }
      }
    } catch (error) {
      Logger.error(error);
    }
  },
  //toggleHelperMicro
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_HELPER_MICRO)]({ state }, isOff: boolean) {
    if (!state.info) return;
    try {
      await state.manager?.setMicrophone({ enable: !isOff });
    } catch (error) {
      notification.error({
        message: fmtMsg(ErrorLocale.ToggleMicroError),
      });
    }
  },
  //toggleHelperCamera
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_HELPER_CAMERA)]({ state }, isOff: boolean) {
    if (!state.info) return;
    try {
      await state.manager?.setCamera({
        enable: !isOff,
        videoEncoderConfigurationPreset: "480p",
      });
    } catch (error) {
      Logger.error(error);
      notification.error({
        message: fmtMsg(ErrorLocale.ToggleCameraError),
      });
    }
  },
  async makeHelperTheNewTeacher({ commit }) {
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.MAKE_HELPER_THE_NEW_TEACHER, false));
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_IGNORE_HELPER_REQUEST_JOIN_CLASS), false, { root: true });
  },
  //changeLocalExposureItem
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.CHANGE_LOCAL_EXPOSURE_ITEM)](
    { commit },
    payload: { exposureId?: string; exposureItemId: string; exposureType?: ExposureDetailType },
  ) {
    if (payload.exposureId) {
      commit(vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE), { id: payload.exposureId }, { root: true });
    }

    /**
     * Update new exposure item and clear the whiteboard in local
     */
    commit(vuexName(MODULES.WHITE_BOARD.COMMITS.RESET_STATE), null, { root: true });
    commit(
      vuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA),
      { id: payload.exposureItemId, exposureType: payload.exposureType },
      { root: true },
    );
    commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_WHITEBOARD), false, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.CLEAR_ZOOM_STATE), null, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS), 0, { root: true });
    commit(vuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS), [], { root: true });
  },
  async updateCameraDevice({ state }, { blockReopen }: { blockReopen?: boolean }) {
    await state.manager?.updateCameraDevice();
    if (blockReopen) return;
    await state.manager?.setCamera({
      enable: false,
      videoEncoderConfigurationPreset: "480p",
    });
    await state.manager?.setCamera({
      enable: true,
      videoEncoderConfigurationPreset: "480p",
    });
  },
  async updateMicrophoneDevice({ state }) {
    await state.manager?.updateMicrophoneDevice();
  },
  async updateSpeakerDevice({ state }) {
    await state.manager?.updateSpeakerDevice();
  },
  //toggleOneToOneWithHelperIgnoreTeacherVoice
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_ONE_TO_ONE_WITH_HELPER_IGNORE_TEACHER_VOICE)]({ commit, rootState }, ignore: boolean) {
    try {
      const request: HelperToggleTeacherAudioModel = {
        isIgnored: ignore,
        sessionId: rootState.classTeaching.sessionId,
      };

      await RemoteTeachingService.helperToggleTeacherAudio(request);
      commit(vuexName(MODULES.CLASS_TEACHING.COMMITS.SET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE), ignore, { root: true });
    } catch (error) {
      Logger.error(error);
    }
  },
  //updateRemotePdfScrollProgress
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.UPDATE_REMOTE_PDF_SCROLL_PROGRESS)](_, progress: number) {
    await RemoteTeachingService.teacherScrollPdf(progress);
  },
  //requestUpdateCanvasObjectAsync
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.REQUEST_UPDATE_CANVAS_OBJECT_ASYNC)]({ state, rootState }, payload: CanvasObjectModel) {
    try {
      await RemoteTeachingService.updateCanvasObject({
        canvasObject: payload,
        sessionId: rootState.classTeaching.sessionId,
        isTeacherUseOnly: state.isTeacherUseOnly,
        isOneOne: !!state.idOne,
      });
    } catch (e) {
      Logger.error(e);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.BROADCAST_MESSAGE_UPDATE_CANVAS_OBJECT)](
    { state, rootState, rootGetters },
    payload: CanvasObjectModel,
  ) {
    const sessionId = rootState.classTeaching.sessionId;
    if (!sessionId) return;
    if (!canSendRealtimeAction(rootGetters, "BROADCAST_MESSAGE_UPDATE_CANVAS_OBJECT")) {
      return;
    }

    const realtimeTransport = state.manager?.WSClient.getRealtimeTransport();
    if (!realtimeTransport) return;

    const isTeacherUseOnly = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_TEACHER_USE_ONLY)];
    const isWhiteboardVisible = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_IS_SHOW_WHITE_BOARD)];
    const oneOneId = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];

    // Teacher use only has its own channel
    if (isTeacherUseOnly) {
      if (!isWhiteboardVisible || oneOneId) {
        // If whiteboard is not visible, no need to broadcast
        return;
      }
      await realtimeTransport.sendToTeacherHelper({
        event: REALTIME_EVENT.TEACHER,
        action: TeacherAction.UPDATE_CANVAS_OBJECT,
        data: payload,
        senderId: rootState.auth.userInfo?.id,
      });
      return;
    }

    // For other cases, validate and get appropriate channel
    const channelInfo = validateAndGetChannel(realtimeTransport, oneOneId, false);

    if (channelInfo) {
      await channelInfo.sendMethod({
        event: REALTIME_EVENT.TEACHER,
        action: TeacherAction.UPDATE_CANVAS_OBJECT,
        data: payload,
        senderId: rootState.auth.userInfo?.id,
      });
    }
  },
  //toggleStudentMediaDevicesAsync
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_STUDENT_MEDIA_DEVICES_ASYNC)](
    { rootState, rootGetters, state },
    payload: ToggleStudentMediaDevicesAsync,
  ) {
    const { mediaDeviceType, studentIds } = payload;
    if (!canSendRealtimeAction(rootGetters, "TOGGLE_STUDENT_MEDIA_DEVICES_ASYNC")) {
      return;
    }

    // Check presence transport availability
    const realtimeTransport = state.manager?.WSClient?.getRealtimeTransport?.();
    if (!realtimeTransport) {
      return;
    }

    if (studentIds.length === 0) {
      return;
    }

    // Validate and get the appropriate channel
    const oneOneId = rootGetters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)];
    const isHelper = rootGetters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)];
    const channelInfo = validateAndGetChannel(realtimeTransport, oneOneId, isHelper);
    if (!channelInfo) {
      return;
    }

    // Require presence to be ready to avoid sending to offline students
    const isPresenceReady = rootGetters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_IS_INITIALIZED)];
    if (!isPresenceReady) {
      showNotification({
        type: AntdNotificationType.WARNING,
        message: fmtMsg(TeacherActionLocale.WaringTitle),
        description: "Unable to determine online/offline status. Please try again once the connection is stable.",
      });
      return;
    }

    // Guard against presence tracking the wrong space channel
    const expectedChannelName = realtimeTransport.getChannelNameByKey?.(channelInfo.channelKey);
    const currentSpaceChannelName = rootGetters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_CHANNEL_NAME)];
    if (expectedChannelName && currentSpaceChannelName && currentSpaceChannelName !== `space-${expectedChannelName}`) {
      showNotification({
        type: AntdNotificationType.WARNING,
        message: fmtMsg(TeacherActionLocale.WaringTitle),
        description: "Presence channel mismatch, please try again in a few seconds.",
      });
      return;
    }

    // Split students by presence
    const { online, offline } = splitStudentsBySpacePresence(rootGetters, studentIds);

    // Notify teacher about offline students
    if (offline.length > 0) {
      const studentMap = new Map(state.students.map((s) => [s.id, { name: s.name, englishName: s.englishName }]));
      const offlineNames = formatMissingStudentNames(offline, studentMap);

      showNotification({
        type: AntdNotificationType.WARNING,
        message: fmtMsg(TeacherActionLocale.WaringTitle),
        description: fmtMsg(TeacherActionLocale.WarningUpdateMediaDeviceFailed, {
          studentNames: offlineNames,
          mediaDeviceType: fmtMsg(
            mediaDeviceType === MediaDeviceTypes.Camera ? TeacherActionLocale.WarningMediaDeviceCamera : TeacherActionLocale.WarningMediaDeviceMicro,
          ),
        }),
      });

      // If all students are offline, abort the operation
      if (online.length === 0) {
        return;
      }
    }

    // Send toggle command to online students only
    try {
      await channelInfo.sendMethod({
        event: REALTIME_EVENT.TEACHER,
        action: TeacherAction.TOGGLE_STUDENT_MEDIA_DEVICES,
        data: {
          ...payload,
          studentIds: online,
        },
        senderId: rootState.auth.userInfo?.id,
      });
    } catch (e) {
      showAPIErrorNotification(e);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_STUDENT_PALETTE_ASYNC)](
    { rootState, state, commit },
    payload: ToggleStudentPaletteAsync,
  ) {
    const { sessionId } = rootState.classTeaching;
    try {
      const resp = await RemoteTeachingService.toggleStudentPalette({ ...payload, sessionId });
      if (!resp.success) {
        const studentName = state.students.find((student) => student.id === payload.studentId)?.name;
        showNotification({
          type: AntdNotificationType.WARNING,
          message: fmtMsg(TeacherActionLocale.WaringTitle),
          description: fmtMsg(TeacherActionLocale.WarningUpdatePaletteFailed, {
            studentName,
          }),
        });
        return;
      }
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_PALETTE, false), {
        studentId: payload.studentId,
        isPaletteEnabled: payload.isEnable,
      });
    } catch (e) {
      showAPIErrorNotification(e);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_STUDENTS_BADGE_ASYNC)]({ rootState }, payload: string[]) {
    try {
      const { sessionId } = rootState.classTeaching;
      await RemoteTeachingService.setStudentsBadge({ sessionId, studentIds: payload });
    } catch (e) {
      showAPIErrorNotification(e);
    }
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.CLEAR_STUDENT_RAISING_HAND_ASYNC)]({ rootState, commit }, studentId: string) {
    try {
      const { sessionId } = rootState.classTeaching;
      const success = await RemoteTeachingService.clearStudentRaisingHand(sessionId, studentId);
      if (success) {
        commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_STUDENT_RAISING_HAND, false), {
          id: studentId,
          raisingHand: false,
        });
      }
    } catch (e) {
      showAPIErrorNotification(e);
    }
  },
  async handleAnotherTeacherToggleStudentMediaDevices() {
    // do nothing
  },
  async [defineVuexName(MODULES.TEACHER_ROOM.DISPATCHES.HANDLE_REMOTE_USER_JOINED)]({ commit, rootState, state }, id: string) {
    const { isHelper: joinAsHelper } = rootState.teacher;
    commit(vuexName(MODULES.CALLING.COMMITS.SET_CALLING_USER_IDS), { id, status: CallingUserStatus.JOINED }, { root: true });
    const student = state.students.find((student) => student.id === id);
    if (student) {
      // do nothing
    } else if (joinAsHelper) {
      commit(vuexName(MODULES.TEACHER_ROOM.COMMITS.SET_TEACHER_DISCONNECTED, false), false);
    }
  },
};

export default actions;
